import { FileVersionConversionModel } from '../../../../typings/api/skymap/rest/internal/file-version';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { maxConvertedFileVersions } from '../asset-manager';
import { subscribe, SubscriptionTopic, unsubscribe } from '../messaging/pubsub';
import { LegacyDrawing } from './collect-report-layers';

/** How long the drawings may take to convert before they're left out of the report. */
const CONVERSION_TIMEOUT_MS = 10 * 60 * 1000;

/** Asks for the versions to be converted, and returns their state. */
async function requestConversion(fileVersionIds: string[]) {
  const service = SkyMapAxiosServiceFactory.instance.createFileVersionServiceInternal();
  const states = new Map<string, FileVersionConversionModel>();

  for (let i = 0; i < fileVersionIds.length; i += maxConvertedFileVersions) {
    const batch = fileVersionIds.slice(i, i + maxConvertedFileVersions);
    const { data } = await service.convertFileVersions({ body: { fileVersionIds: batch } });
    data.forEach((model) => states.set(model.fileVersionId, model));
  }

  return states;
}

const isConverted = (model: FileVersionConversionModel | undefined) =>
  model?.state === 'CONVERTED' && (model.glb === undefined || model.glb.status === 'COMPLETED');

/**
 * Converts the versions of drawings shown from their legacy DXF to glb, as the backend draws the
 * report from glbs, and waits until they are converted. The backend skips versions that already
 * have a glb, and sends DynamicAssetUpdated to a drawing's assets when its conversion has finished.
 * SkyView shows the drawings from their DXF as before.
 *
 * @returns The versions that couldn't be converted, or not in time, to leave out of the report.
 */
export async function prepareDrawings(drawings: LegacyDrawing[]): Promise<Set<string>> {
  const unconverted = new Set<string>();
  if (drawings.length === 0) {
    return unconverted;
  }

  const deadline = Date.now() + CONVERSION_TIMEOUT_MS;
  const updatedAssetIds = new Set<string>();
  let onUpdate: (() => void) | undefined;

  // A conversion may finish before its request returns, so it listens first.
  const subscription = subscribe(SubscriptionTopic.WebsocketMessageRecieved, (message) => {
    if (message?.message === 'DynamicAssetUpdated') {
      updatedAssetIds.add(message.dynamicAssetId);
      onUpdate?.();
    }
  });

  /** Waits for one of the assets to be updated, false if the time is up first. */
  const waitForUpdate = (assetIds: string[]) =>
    new Promise<boolean>((resolve) => {
      const isUpdated = () => assetIds.some((id) => updatedAssetIds.has(id));
      if (isUpdated()) {
        resolve(true);
        return;
      }

      const timeout = setTimeout(() => {
        onUpdate = undefined;
        resolve(false);
      }, deadline - Date.now());
      onUpdate = () => {
        if (isUpdated()) {
          clearTimeout(timeout);
          onUpdate = undefined;
          resolve(true);
        }
      };
    });

  try {
    let pending = [...new Set(drawings.map((x) => x.fileVersionId))];

    while (pending.length > 0) {
      updatedAssetIds.clear();
      const states = await requestConversion(pending);

      pending
        .filter((id) => states.get(id)?.state !== 'CONVERTING' && !isConverted(states.get(id)))
        .forEach((id) => unconverted.add(id));
      pending = pending.filter((id) => states.get(id)?.state === 'CONVERTING');

      const assetIds = drawings
        .filter((x) => pending.includes(x.fileVersionId))
        .map((x) => x.assetId);
      if (pending.length > 0 && !(await waitForUpdate(assetIds))) {
        pending.forEach((id) => unconverted.add(id));
        break;
      }
    }
  } finally {
    unsubscribe(subscription);
  }

  return unconverted;
}
